import FileDownloadIcon from '@mui/icons-material/FileDownload';
import type * as React from 'react';
import {useState} from 'react';
import {MaterialReactTable, type MRT_Row, type MRT_RowSelectionState,} from 'material-react-table';
import type {CourseApproval} from '@/types/CourseApproval';
import {Box, Button, Stack} from '@mui/material';
import SelectDepartmentFilter from '@/components/CourseApproval/SelectDepartmentFilter';
import DialogResources, {type ResourcesModalState} from '@/components/CourseApproval/DialogResources';
import {download, generateCsv, mkConfig} from 'export-to-csv';
import DialogChangeRequestForm, {type RequestModalState} from './DialogChangeRequestForm';
import ApproveSelectedCoursesModal from '@/components/CourseApproval/DialogApproveSelectedCourses';
import {type MaterialExport} from '@/types/MaterialExport';

type Props = {
    courseApprovals: CourseApproval[];
};

const TableCourseApprovals = ({courseApprovals}: Props): React.ReactElement => {
    const [modalState, setModalState] = useState<ResourcesModalState>({open: false});
    const [requestModalState, setRequestModalState] = useState<RequestModalState>({open: false});
    const courseApprovalNeeded = courseApprovals.filter((c) => !c.approved).length > 0;
    const uniqueDepartments = [...new Set(courseApprovals.map((c) => c.department))];
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

    const columns = [
        {
            accessorKey: 'compoundKey',
            header: 'ID',
            sort: true,
            accessorFn: (row: CourseApproval) => row.compoundKey,
        },
        {
            accessorKey: 'department',
            header: 'Department',
            sort: true,
        },
        {
            accessorKey: 'schoolShortName',
            header: 'Building',
            sort: true,
        },
        {
            accessorKey: 'courseNumber',
            header: 'Course Number',
            sort: true,
        },
        {
            accessorKey: 'sectionNumber',
            header: 'Section Number',
            sort: true,
        },
        {
            accessorKey: 'year',
            header: 'Year',
            sort: true,
        },
        {
            accessorKey: 'resources',
            header: 'Resources',
            accessorFn: (row: CourseApproval) =>
                row.materialRequirements.length > 0 ? (
                    <Button
                        onClick={() =>
                            setModalState({
                                open: true,
                                title: `${row.schoolShortName} - ${row.courseNumber} - ${row.sectionNumber}`,
                                materialRequirements: row.materialRequirements,
                            })
                        }
                    >
                        View Resources
                    </Button>
                ) : (
                    ''
                ),
            sort: true,
        },
    ];

    const csvConfig = mkConfig({
        filename: 'CourseApprovals',
        fieldSeparator: ',',
        decimalSeparator: '.',
        useBom: true,
        useKeysAsHeaders: false,
        columnHeaders: [
            {key: 'department', displayLabel: 'Department'},
            {key: 'schoolShortName', displayLabel: 'Building'},
            {key: 'courseNumber', displayLabel: 'Course Number'},
            {key: 'sectionNumber', displayLabel: 'Section Number'},
            {key: 'year', displayLabel: 'Year'},
            {key: 'description', displayLabel: 'Description'},
            {key: 'manufacturer', displayLabel: 'Manufacturer'},
            {key: 'subType', displayLabel: 'SubType'},
            {key: 'type', displayLabel: 'Type'},
        ],
    });

    const handleExportRows = (rows: MRT_Row<CourseApproval>[]) => {
        const materialsExport: MaterialExport[] = [];

        for (const row of rows) {
            for (const material of row.original.materialRequirements || []) {
                materialsExport.push({
                    department: row.original.department,
                    schoolShortName: row.original.schoolShortName,
                    courseNumber: row.original.courseNumber,
                    sectionNumber: row.original.sectionNumber,
                    year: row.original.year,
                    description: material.description,
                    manufacturer: material.manufacturer,
                    subType: material.subType,
                    type: material.type,
                });
            }
        }

        const csv = generateCsv(csvConfig)(materialsExport);
        download(csvConfig)(csv);
    };

    return (
        <MaterialReactTable
            data={courseApprovals}
            columns={columns}
            getRowId={(row) => row.compoundKey}
            state={{rowSelection}}
            onRowSelectionChange={setRowSelection}
            enableRowSelection={courseApprovalNeeded ? (row) => !row.original.approved : false}
            enableFacetedValues
            enableHiding={false}
            initialState={{columnVisibility: {compoundKey: false}}}
            enableColumnActions={false}
            enablePagination
            enableColumnFilters={false}
            enableFullScreenToggle={false}
            enableDensityToggle={false}
            enableGlobalFilter={false}
            enableFilterMatchHighlighting={false}
            positionActionsColumn="last"
            enableRowActions
            displayColumnDefOptions={{
                'mrt-row-actions': {size: 250},
            }}
            renderTopToolbarCustomActions={({table}) => (
                <Stack sx={{flexDirection: 'column', width: '100%'}}>
                    <Stack sx={{flexDirection: 'row', justifyContent: 'center', width: '100%'}}>
                        <Box sx={{typography: 'h5', p: 2}}>Course Approvals</Box>
                    </Stack>
                    <Stack sx={{flexDirection: 'row', justifyContent: 'space-between', width: '100%'}}>
                        {courseApprovalNeeded && (
                            <ApproveSelectedCoursesModal
                                setRowSelection={setRowSelection}
                                selectedCourseApprovals={courseApprovals.filter((c) =>
                                    rowSelection.hasOwnProperty(c.compoundKey),
                                )}
                            />
                        )}
                        <SelectDepartmentFilter
                            id="department"
                            label="Department"
                            values={uniqueDepartments}
                            boxSx={{minWidth: '300px'}}
                            onChange={(value) => {
                                if (value === '') {
                                    table.resetColumnFilters();
                                } else {
                                    table.setColumnFilters((prev) => [
                                        ...prev.filter((f) => f.id !== 'department'),
                                        {id: 'department', value},
                                    ]);
                                }
                            }}
                        />
                        <Button
                            disabled={table.getPrePaginationRowModel().rows.length === 0}
                            onClick={() => handleExportRows(table.getPrePaginationRowModel().rows)}
                            startIcon={<FileDownloadIcon/>}
                            variant="contained"
                        >
                            Export All Rows
                        </Button>
                    </Stack>
                </Stack>
            )}
            renderRowActions={({row}) => {
                const originalRow = row.original;
                const title = `${originalRow.schoolShortName} - ${originalRow.courseNumber} - ${originalRow.sectionNumber}`;

                return (
                    <Box sx={{display: 'flex', flexWrap: 'nowrap', gap: '8px'}}>
                        <Button
                            color={originalRow.request ? 'secondary' : 'primary'}
                            onClick={() =>
                                setRequestModalState({
                                    open: true,
                                    title,
                                    courseApproval: originalRow,
                                })
                            }
                        >
                            {originalRow.request ? 'Revise Change Request' : 'Add Change Request'}
                        </Button>
                    </Box>
                );
            }}
            renderBottomToolbarCustomActions={() => (
                <Box>
                    <DialogResources modalState={modalState} setModalState={setModalState}/>
                    <DialogChangeRequestForm
                        modalState={requestModalState}
                        setModalState={setRequestModalState}
                    />
                </Box>
            )}
        />
    );
};

export default TableCourseApprovals;
